<template>
  <div class="page-content">
    <!-- Header -->
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="items" />
        <h1 class="page-title">Rapport de Ventes</h1>
      </div>
    </div>

    <PreloaderComponent :enable="enable" />

    <div v-if="enable">
      <!-- Filters -->
      <v-card flat class="filter-card mb-4">
        <v-card-text class="py-3">
          <v-row align="center" dense>
            <v-col cols="12" sm="6" md="2">
              <v-select
                :items="stores"
                item-value="id"
                item-text="name"
                placeholder="Emplacement"
                prepend-inner-icon="mdi-store-outline"
                v-model="store_id"
                outlined
                dense
                hide-details
                clearable
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                :items="users"
                item-value="id"
                item-text="full_name"
                placeholder="Agent"
                v-model="user_id"
                outlined
                dense
                hide-details
                clearable
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-select
                :items="statuses"
                item-value="id"
                item-text="label"
                placeholder="Statut"
                v-model="status"
                outlined
                dense
                hide-details
                clearable
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-text-field
                v-model="sale_number"
                placeholder="# de Vente"
                outlined
                dense
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="auto">
              <div class="d-flex gap-2">
                <v-btn color="primary" depressed small @click="filter" :loading="loading" class="mr-1">
                  <v-icon small>mdi-check</v-icon>
                </v-btn>
                <v-btn outlined small @click="reset" :loading="loading" class="mr-1">
                  <v-icon small>mdi-refresh</v-icon>
                </v-btn>
                <v-btn color="error" outlined small @click="exportPDF" class="mr-1">
                  <v-icon small>mdi-file-pdf-box</v-icon>
                </v-btn>
                <v-btn color="success" outlined small @click="exportExcel">
                  <v-icon small>mdi-file-excel</v-icon>
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Totaux globaux (ventes actives uniquement) -->
      <div class="mb-3">
        <v-row class="sales-summary-row" dense>
          <v-col cols="12" sm="6" md="4">
            <v-card flat class="summary-total-card pa-4">
              <div class="summary-label">Total HTG</div>
              <div class="summary-value summary-value--htg">{{ total(3) | formatNumber }} HTG</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-card flat class="summary-total-card pa-4">
              <div class="summary-label">Total USD</div>
              <div class="summary-value summary-value--usd">{{ total(4) | formatNumber }} USD</div>
            </v-card>
          </v-col>
        </v-row>
        <p class="summary-hint mb-0 mt-2">{{ activeSalesCount }} vente(s) active(s) dans la période (hors annulées)</p>
      </div>

      <!-- Sales table -->
      <v-card flat class="table-card">
        <div class="table-responsive">
          <table class="report-table" id="content">
            <thead>
              <tr>
                <th class="text-left">#</th>
                <th>EMPLACEMENT</th>
                <th>AGENT</th>
                <th>CLIENT</th>
                <th v-if="filter_rate_id == 1">HTG</th>
                <th v-if="filter_rate_id == 2">USD</th>
                <th v-if="filter_rate_id == 3">HTG</th>
                <th v-if="filter_rate_id == 3">USD</th>
                <th v-if="hasUsdStores && filter_rate_id == 1">ÉQUIV. USD</th>
                <th>PRODUITS</th>
                <th>DATE VENTE</th>
                <th>DATE SYNC</th>
                <th>STATUT</th>
                <th></th>
              </tr>
              <tr class="totals-row">
                <th colspan="4" class="text-left">TOTAL ({{ activeSalesCount }} fiches actives / {{ rows.length }} total)</th>
                <th v-if="filter_rate_id == 1">{{ total(1) | formatNumber }}</th>
                <th v-if="filter_rate_id == 2">{{ total(2) | formatNumber }}</th>
                <th v-if="filter_rate_id == 3">{{ total(3) | formatNumber }}</th>
                <th v-if="filter_rate_id == 3">{{ total(4) | formatNumber }}</th>
                <th v-if="hasUsdStores && filter_rate_id == 1">{{ totalUsdEquiv() | formatNumber }} USD</th>
                <th>{{ products() }}</th>
                <th colspan="4"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="sale in rows" :key="sale.id">
                <td class="text-left">{{ sale.sale_number }}</td>
                <td>{{ sale.store.name }}</td>
                <td>{{ sale.user.full_name }}</td>
                <td>{{ sale.customer.full_name }}</td>
                <td v-if="filter_rate_id == 1">{{ formatMoneyOrDash(displayAmountHtg(sale)) }}</td>
                <td v-if="filter_rate_id == 2">{{ formatMoneyOrDash(displayAmountUsd(sale)) }}</td>
                <td v-if="filter_rate_id == 3">{{ formatMoneyOrDash(displayAmountHtg(sale)) }}</td>
                <td v-if="filter_rate_id == 3">{{ formatMoneyOrDash(displayAmountUsd(sale)) }}</td>
                <td v-if="hasUsdStores && filter_rate_id == 1">{{ formatMoneyOrDash(displayAmountUsd(sale)) }}</td>
                <td>
                  <v-chip x-small color="info" dark>{{ detailProducts(sale.sale_products) }}</v-chip>
                </td>
                <td class="text-caption">{{ sale.created_at | formatDate }} {{ sale.created_at | formatTime }}</td>
                <td class="text-caption">{{ sale.updated_at | formatDate }} {{ sale.updated_at | formatTime }}</td>
                <td>
                  <v-chip x-small :color="sale.status == 1 ? 'success' : 'error'" dark>
                    {{ sale.status == 1 ? 'Actif' : 'Annulé' }}
                  </v-chip>
                </td>
                <td>
                  <v-btn icon x-small @click="view(sale.id)">
                    <v-icon small color="secondary">mdi-eye-outline</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'
import PreloaderComponent from '../components/PreloaderComponent.vue'
import { exportReportPDF, exportReportExcel } from '@/utils/reportExport'

export default {
  name: 'SalesReportView',
  components: { BreadcrumbComponent, PreloaderComponent },
  data: () => ({
    items: [{ text: 'Rapport Ventes', disabled: false, href: '/' }],
    filter_rate_id: 1,
    rows: [],
    stores: [],
    enable: false,
    loading: false,
    store_id: '',
    sale_number: '',
    business_id: 0,
    user_id: '',
    users: [],
    status: '',
    statuses: [{ id: 1, label: 'Actif' }, { id: 0, label: 'Annulé' }],
    hasUsdStores: false,
  }),
  computed: {
    activeSalesCount() {
      return this.rows.filter(function (s) { return s.status == 1 }).length
    },
  },
  methods: {
    /** Taux journalier utilisable pour passer HTG ↔ USD (depuis la vente). */
    saleConversionRate(sale) {
      const dr = parseFloat(sale.daily_rate)
      if (!dr || dr <= 0 || Number.isNaN(dr)) return null
      // Vente en HTG avec daily_rate = 1 : pas de conversion fiable (évite total/1 affiché en USD).
      if (Number(sale.rate_id) === 1 && dr <= 1) return null
      return dr
    },
    displayAmountHtg(sale) {
      const t = parseFloat(sale.total)
      if (Number.isNaN(t)) return null
      if (Number(sale.rate_id) === 1) return t
      const dr = this.saleConversionRate(sale)
      if (dr == null) return null
      return t * dr
    },
    displayAmountUsd(sale) {
      const t = parseFloat(sale.total)
      if (Number.isNaN(t)) return null
      if (Number(sale.rate_id) === 2) return t
      const dr = this.saleConversionRate(sale)
      if (dr == null) return null
      return t / dr
    },
    formatMoneyOrDash(val) {
      if (val == null || Number.isNaN(val)) return '—'
      return this.$options.filters.formatNumber(val)
    },
    totalUsdEquiv() {
      var sum = 0
      for (var i = this.rows.length - 1; i >= 0; i--) {
        if (this.rows[i].status != 1) continue
        var v = this.displayAmountUsd(this.rows[i])
        if (v != null && !Number.isNaN(v)) sum += v
      }
      return sum
    },
    load() {
      const isValid = (val) => val && val !== 'null' && val.trim() !== ''
      const debutRaw = isValid(localStorage.debut_periode.toLocaleString()) ? localStorage.debut_periode.toLocaleString() : ''
      const finRaw = isValid(localStorage.fin_periode.toLocaleString()) ? localStorage.fin_periode.toLocaleString() : ''
      var params = ''
      if (isValid(debutRaw) && isValid(finRaw)) params += '&from=' + debutRaw + '&to=' + finRaw

      var self = this
      this.$axios.get('/sales?orderdesc=created_at&include=user,store,customer,sale_products,rate' + params)
        .then(function (response) {
          self.rows = response.data.filter(function(s) { return parseFloat(s.total) >= 0 })
          self.enable = true
        })
    },
    detailProducts(products) {
      if (!products || !products.length) return 0
      return products.reduce((total, p) => total + parseInt(p.quantity), 0)
    },
    view(id) { this.$router.push('/sales/' + id) },
    reset() { this.store_id = ''; this.sale_number = ''; this.user_id = ''; this.status = ''; this.filter() },
    total(rate_id) {
      var sum = 0
      for (var i = this.rows.length - 1; i >= 0; i--) {
        if (this.rows[i].status != 1) continue
        var v = null
        if (rate_id == 1 || rate_id == 3) v = this.displayAmountHtg(this.rows[i])
        else if (rate_id == 2 || rate_id == 4) v = this.displayAmountUsd(this.rows[i])
        if (v != null && !Number.isNaN(v)) sum += v
      }
      return sum
    },
    filter() {
      const isValid = (val) => val && val !== 'null' && val.trim() !== ''
      const debutRaw = isValid(localStorage.debut_periode.toLocaleString()) ? localStorage.debut_periode.toLocaleString() : ''
      const finRaw = isValid(localStorage.fin_periode.toLocaleString()) ? localStorage.fin_periode.toLocaleString() : ''
      var params = ''
      if (isValid(debutRaw) && isValid(finRaw)) params += '&from=' + debutRaw + '&to=' + finRaw
      if (this.store_id) params += '&store_id=' + this.store_id
      if (this.sale_number) params += '&sale_number=' + this.sale_number
      if (this.user_id) params += '&user_id=' + this.user_id
      if (this.status === 0 || this.status === 1) params += '&status=' + this.status

      this.enable = false
      var self = this
      this.$axios.get('/sales?orderdesc=created_at&include=user,store,customer,sale_products,rate' + params)
        .then(function (response) {
          self.rows = response.data.filter(function(s) { return parseFloat(s.total) >= 0 })
          self.enable = true
        })
    },
    async exportPDF() {
      await exportReportPDF(this.rows, this.filter_rate_id, {
        title: 'Rapport de Ventes',
        headerColor: [25, 65, 115],
        filename: 'rapport_ventes.pdf',
      })
    },
    exportExcel() {
      exportReportExcel(this.rows, this.filter_rate_id, {
        title: 'Ventes',
        headerColorHex: '194173',
        filename: 'rapport_ventes.xlsx',
      })
    },
    products() {
      var products = 0
      for (var i = this.rows.length - 1; i >= 0; i--) {
        if (this.rows[i].status == 1) {
          for (var j = this.rows[i].sale_products.length - 1; j >= 0; j--) {
            products += parseFloat(this.rows[i].sale_products[j].quantity)
          }
        }
      }
      return products
    },
    async getStores() {
      this.business_id = JSON.parse(localStorage.business).id
      var self = this
      await this.$axios.get('/stores?orderasc=name&business_id=' + this.business_id).then(function (response) {
        self.stores = response.data || []
        self.hasUsdStores = self.stores.some(function (s) { return Number(s.rate_id) === 2 })
      })
    },
    async getUsers() {
      var self = this
      await this.$axios.get('/users').then(function (response) { self.users = response.data })
    },
  },
  created() {
    this.filter_rate_id = localStorage.filter_rate_id
    this.load()
    this.getUsers()
    this.getStores()
  },
}
</script>

<style scoped>
.filter-card {
  border: 1px solid #e8ecf1 !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important;
}

.table-card {
  border: 1px solid #e8ecf1 !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important;
  overflow: hidden;
}

.table-responsive {
  overflow-x: auto;
  max-height: 600px;
  overflow-y: auto;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
}

.report-table thead th {
  position: sticky;
  top: 0;
  background: #f8fafc;
  font-weight: 600;
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  padding: 12px;
  text-align: center;
  border-bottom: 2px solid #e8ecf1;
  white-space: nowrap;
  z-index: 1;
}

.report-table .totals-row th {
  background: #f1f5f9;
  color: #1a2332;
  font-weight: 700;
  font-size: 0.75rem;
  border-bottom: 2px solid #cbd5e1;
}

.report-table tbody td {
  padding: 10px 12px;
  text-align: center;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap;
}

.report-table tbody tr:hover {
  background-color: rgba(25, 65, 115, 0.03);
}

.gap-2 {
  gap: 4px;
}

.summary-total-card {
  border: 1px solid #e8ecf1 !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
  height: 100%;
}

.summary-label {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
  margin-bottom: 4px;
}

.summary-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a2332;
  line-height: 1.2;
}

.summary-value--htg {
  color: #f68d3b;
}

.summary-value--usd {
  color: #194173;
}

.summary-hint {
  font-size: 0.6875rem;
  color: #94a3b8;
  margin-top: 6px;
}
</style>
